import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, Button, Badge, Modal } from 'react-bootstrap';
import { fetchPayments, deletePayment } from '../../redux/slices/paymentsSlice';
import { Link } from 'react-router-dom';

const getPaymentStatus = (payment) => {
  const paidCount = payment.installments.filter(i => i.paid).length;
  const totalCount = payment.installments.length;
  if (paidCount === totalCount) return 'completed';
  if (paidCount > 0) return 'in_progress';
  return 'pending';
};

const STATUS_CONFIG = {
  all:         { label: 'Todos',       color: '#0d6efd', bg: 'primary',   border: '#0d6efd' },
  in_progress: { label: 'En Curso',    color: '#ffc107', bg: 'warning',   border: '#ffc107' },
  completed:   { label: 'Completados', color: '#198754', bg: 'success',   border: '#198754' },
  pending:     { label: 'Pendientes',  color: '#dc3545', bg: 'danger',    border: '#dc3545' },
};

const PaymentList = () => {
  const dispatch = useDispatch();
  const { payments, loading } = useSelector((state) => state.payments);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    dispatch(fetchPayments());
  }, [dispatch]);

  const handleDeleteClick = (payment) => {
    setSelectedPayment(payment);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedPayment) {
      await dispatch(deletePayment(selectedPayment.id));
      setShowDeleteModal(false);
      setSelectedPayment(null);
      dispatch(fetchPayments());
    }
  };

  const counts = {
    all: payments.length,
    in_progress: payments.filter(p => getPaymentStatus(p) === 'in_progress').length,
    completed:   payments.filter(p => getPaymentStatus(p) === 'completed').length,
    pending:     payments.filter(p => getPaymentStatus(p) === 'pending').length,
  };

  const filteredPayments = activeFilter === 'all'
    ? payments
    : payments.filter(p => getPaymentStatus(p) === activeFilter);

  if (loading) {
    return <Container className="mt-4"><div className="text-center">Cargando...</div></Container>;
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col><h2>Mis Microcréditos</h2></Col>
        <Col className="text-end">
          <Button as={Link} to="/payments/new" variant="primary">Nuevo Microcrédito</Button>
        </Col>
      </Row>

      {/* Status filter cards */}
      <Row className="mb-4 g-3">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const isActive = activeFilter === key;
          return (
            <Col xs={6} md={3} key={key}>
              <Card
                onClick={() => setActiveFilter(key)}
                style={{
                  cursor: 'pointer',
                  borderWidth: 2,
                  borderColor: isActive ? cfg.color : '#dee2e6',
                  backgroundColor: isActive ? cfg.color : '#fff',
                  color: isActive ? '#fff' : '#212529',
                  transition: 'all 0.2s ease',
                  boxShadow: isActive ? `0 4px 12px ${cfg.color}55` : 'none',
                }}
              >
                <Card.Body className="text-center py-3">
                  <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>
                    {counts[key]}
                  </div>
                  <div style={{ fontSize: '0.85rem', marginTop: 4, opacity: isActive ? 1 : 0.7 }}>
                    {cfg.label}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Payment cards */}
      {payments.length === 0 ? (
        <Card>
          <Card.Body className="text-center">
            <p>No tienes microcréditos registrados</p>
            <Button as={Link} to="/payments/new" variant="primary">
              Crear tu primer microcrédito
            </Button>
          </Card.Body>
        </Card>
      ) : filteredPayments.length === 0 ? (
        <Card>
          <Card.Body className="text-center text-muted">
            No hay microcréditos con estado "{STATUS_CONFIG[activeFilter].label}"
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-3">
          {filteredPayments.map((payment) => {
            const statusKey = getPaymentStatus(payment);
            const cfg = STATUS_CONFIG[statusKey];
            const paidCount = payment.installments.filter(i => i.paid).length;
            const paidAmount = payment.installments
              .filter(i => i.paid)
              .reduce((sum, i) => sum + i.amount, 0);
            const pendingAmount = payment.total_amount - paidAmount;

            return (
              <Col md={6} lg={4} key={payment.id}>
                <Card style={{ borderLeft: `4px solid ${cfg.color}`, height: '100%' }}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Card.Title className="mb-0" style={{ fontSize: '1rem' }}>
                        {payment.title}
                      </Card.Title>
                      <Badge bg={cfg.bg} style={{ whiteSpace: 'nowrap' }}>{cfg.label}</Badge>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Cuotas pagadas</small>
                        <small><strong>{paidCount}/{payment.installments_count}</strong></small>
                      </div>
                      <div style={{ height: 6, backgroundColor: '#e9ecef', borderRadius: 3 }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${(paidCount / payment.installments_count) * 100}%`,
                            backgroundColor: cfg.color,
                            borderRadius: 3,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ fontSize: '0.875rem' }}>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Total:</span>
                        <strong>${payment.total_amount.toFixed(2)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Pagado:</span>
                        <span className="text-success">${paidAmount.toFixed(2)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Pendiente:</span>
                        <span className="text-danger">${pendingAmount.toFixed(2)}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Día de pago:</span>
                        <span>{payment.payment_day}</span>
                      </div>
                    </div>

                    <div className="mt-3 d-flex gap-2">
                      <Button
                        as={Link}
                        to={`/payments/${payment.id}`}
                        variant="outline-primary"
                        size="sm"
                        className="flex-grow-1"
                      >
                        Ver Detalles
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(payment)}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas eliminar el microcrédito "{selectedPayment?.title}"?
          Esta acción no se puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>Eliminar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PaymentList;
